import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from "@mui/icons-material";

const Pagination = ({ itemsPerPage, totalItems, paginate, currentPage }) => {
  // On utilise useState pour gérer le maxPageButtons qui change selon la taille d'écran
  const [maxPageButtons, setMaxPageButtons] = useState(5);
  
  // Utiliser useEffect pour détecter la taille de l'écran
  useEffect(() => {
    // Fonction pour mettre à jour le nombre de boutons selon la taille d'écran
    const updateMaxButtons = () => {
      if (window.innerWidth >= 1280) { // Tailwind xl breakpoint (1280px)
        setMaxPageButtons(10);
      } else {
        setMaxPageButtons(5);
      }
    };
    
    // Appel initial
    updateMaxButtons();
    
    // Ajouter un écouteur d'événement pour les changements de taille
    window.addEventListener('resize', updateMaxButtons);
    
    // Nettoyage lors du démontage du composant
    return () => window.removeEventListener('resize', updateMaxButtons);
  }, []);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  // Fonction pour générer les numéros de page avec ellipses
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Si le nombre total de pages est inférieur ou égal au nombre maximum de boutons à afficher
    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
      return pageNumbers;
    }
    
    // Nombre de boutons autour de la page courante (de chaque côté)
    const siblingsCount = Math.floor((maxPageButtons - 3) / 2); // -3 pour première page, dernière page et page courante
    
    // Toujours afficher la première page
    pageNumbers.push(1);
    
    // Calculer la plage de pages à afficher autour de la page courante
    let startPage = Math.max(2, currentPage - siblingsCount);
    let endPage = Math.min(totalPages - 1, currentPage + siblingsCount);
    
    // Ajuster si on est proche du début
    if (currentPage - siblingsCount <= 2) {
      endPage = Math.min(totalPages - 1, maxPageButtons - 2);
    }
    
    // Ajuster si on est proche de la fin
    if (currentPage + siblingsCount >= totalPages - 1) {
      startPage = Math.max(2, totalPages - (maxPageButtons - 2));
    }
    
    // Ajouter ellipse si nécessaire au début
    if (startPage > 2) {
      pageNumbers.push('...');
    }
    
    // Ajouter les pages du milieu
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // Ajouter ellipse si nécessaire à la fin
    if (endPage < totalPages - 1) {
      pageNumbers.push('...');
    }
    
    // Toujours afficher la dernière page (sauf si c'est la première)
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  // Version mobile
  const MobilePagination = () => (
    <div className="flex items-center justify-between ml-2 mr-2">
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`flex items-center px-4 py-2 rounded-lg ${
          currentPage === 1 
            ? 'bg-gray-100 text-gray-400' 
            : 'bg-zemusbis text-white'
        }`}
      >
        <ArrowLeft size={20} className="mr-1" />
        Précédent
      </button>

      <span className="text-sm text-gray-600">
        Page {currentPage} sur {totalPages}
      </span>

      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={`flex items-center px-4 py-2 rounded-lg ${
          currentPage === totalPages 
            ? 'bg-gray-100 text-gray-400' 
            : 'bg-zemusbis text-white'
        }`}
      >
        Suivant
        <ArrowRight size={20} className="ml-1" />
      </button>
    </div>
  );

  // Version desktop améliorée
  const DesktopPagination = () => (
    <div className="flex items-center justify-center space-x-2">
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`p-2 rounded-lg ${
          currentPage === 1 
            ? 'bg-gray-100 text-gray-400' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        <ArrowLeft size={20} />
      </button>

      {getPageNumbers().map((item, index) => (
        item === '...' ? (
          <span key={`ellipsis-${index}`} className="px-3 py-2">
            ...
          </span>
        ) : (
          <button
            key={`page-${item}`}
            onClick={() => paginate(item)}
            className={`px-4 py-2 rounded-lg ${
              currentPage === item 
                ? 'bg-zemusbis text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {item}
          </button>
        )
      ))}

      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-lg ${
          currentPage === totalPages 
            ? 'bg-gray-100 text-gray-400' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        <ArrowRight size={20} />
      </button>
    </div>
  );

  return (
    <nav className="mt-4">
      <div className="block md:hidden">
        <MobilePagination />
      </div>
      <div className="hidden md:block">
        <DesktopPagination />
      </div>
    </nav>
  );
};

export default Pagination;